import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

// ─── WebGL Shader Background ────────────────────────────────────────────────

const defaultShaderSource = `#version 300 es
/*
 * Adapted shader — Mikel Media brand palette
 * Original by Matthias Hurrle (@atzedent)
 */
precision highp float;
out vec4 O;
uniform vec2 resolution;
uniform float time;
#define FC gl_FragCoord.xy
#define T time
#define R resolution
#define MN min(R.x,R.y)

float rnd(vec2 p) {
  p=fract(p*vec2(12.9898,78.233));
  p+=dot(p,p+34.56);
  return fract(p.x*p.y);
}
float noise(in vec2 p) {
  vec2 i=floor(p), f=fract(p), u=f*f*(3.-2.*f);
  float a=rnd(i), b=rnd(i+vec2(1,0)), c=rnd(i+vec2(0,1)), d=rnd(i+1.);
  return mix(mix(a,b,u.x),mix(c,d,u.x),u.y);
}
float fbm(vec2 p) {
  float t=.0, a=1.; mat2 m=mat2(1.,-.5,.2,1.2);
  for (int i=0; i<5; i++) {
    t+=a*noise(p);
    p*=2.*m;
    a*=.5;
  }
  return t;
}
float clouds(vec2 p) {
  float d=1., t=.0;
  for (float i=.0; i<3.; i++) {
    float a=d*fbm(i*10.+p.x*.2+.2*(1.+i)*p.y+d+i*i+p);
    t=mix(t,d,a);
    d=a;
    p*=2./(i+1.);
  }
  return t;
}
void main(void) {
  vec2 uv=(FC-.5*R)/MN, st=uv*vec2(2,1);
  vec3 col=vec3(0);

  // Brand palette: deep navy #233559, purple #7D468C, teal #4EBFBF, coral #F2785C
  vec3 navy  = vec3(0.137, 0.208, 0.349);
  vec3 purple= vec3(0.490, 0.275, 0.549);
  vec3 teal  = vec3(0.306, 0.749, 0.749);
  vec3 coral = vec3(0.949, 0.471, 0.361);

  float bg = clouds(vec2(st.x+T*.5, -st.y));
  uv *= 1.-.3*(sin(T*.2)*.5+.5);

  for (float i=1.; i<12.; i++) {
    uv += .1*cos(i*vec2(.1+.01*i, .8)+i*i+T*.5+.1*uv.x);
    vec2 p = uv;
    float d = length(p);

    // Mix brand colors instead of generic warm palette
    vec3 c1 = mix(navy, purple, sin(i*.7+T*.3)*.5+.5);
    vec3 c2 = mix(teal, coral,  sin(i*.4+T*.2)*.5+.5);
    vec3 brandCol = mix(c1, c2, sin(i*.3)*.5+.5);

    col += .00125/d*(brandCol+.3);
    float b = noise(i+p+bg*1.731);
    col += .002*b/length(max(p, vec2(b*p.x*.02, p.y)));
    col  = mix(col, navy*bg*.6 + purple*bg*.15, d*.6);
  }
  O = vec4(col, 1);
}`;

const useShaderBackground = () => {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef();
  const rendererRef = useRef(null);
  const pointersRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl2');
    if (!gl) return;

    // ── Helpers ──────────────────────────────────────────────────────────────
    const compileShader = (type, src) => {
      const s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        console.error('Shader error:', gl.getShaderInfoLog(s));
        gl.deleteShader(s);
        return null;
      }
      return s;
    };

    const vertSrc = `#version 300 es
precision highp float;
in vec4 position;
void main(){ gl_Position = position; }`;

    const vs = compileShader(gl.VERTEX_SHADER, vertSrc);
    const fs = compileShader(gl.FRAGMENT_SHADER, defaultShaderSource);
    if (!vs || !fs) return;

    const program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('Program link error:', gl.getProgramInfoLog(program));
      return;
    }

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,1,-1,-1,1,1,1,-1]), gl.STATIC_DRAW);
    const pos = gl.getAttribLocation(program, 'position');
    gl.enableVertexAttribArray(pos);
    gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);

    const uRes     = gl.getUniformLocation(program, 'resolution');
    const uTime    = gl.getUniformLocation(program, 'time');
    const uMove    = gl.getUniformLocation(program, 'move');
    const uTouch   = gl.getUniformLocation(program, 'touch');
    const uPCount  = gl.getUniformLocation(program, 'pointerCount');
    const uPointers= gl.getUniformLocation(program, 'pointers');

    // ── Pointer state ─────────────────────────────────────────────────────────
    let mouseX = 0, mouseY = 0;
    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      mouseX = e.clientX - r.left;
      mouseY = e.clientY - r.top;
    };
    canvas.addEventListener('pointermove', onMove);

    // ── Resize ────────────────────────────────────────────────────────────────
    const resize = () => {
      const dpr = Math.max(1, 0.5 * window.devicePixelRatio);
      canvas.width  = window.innerWidth  * dpr;
      canvas.height = window.innerHeight * dpr;
      gl.viewport(0, 0, canvas.width, canvas.height);
    };
    resize();
    window.addEventListener('resize', resize);

    // ── Render loop ───────────────────────────────────────────────────────────
    const loop = (now) => {
      gl.clearColor(0,0,0,1);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(program);
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.uniform2f(uRes, canvas.width, canvas.height);
      gl.uniform1f(uTime, now * 1e-3);
      gl.uniform2f(uMove, 0, 0);
      gl.uniform2f(uTouch, mouseX, canvas.height - mouseY);
      gl.uniform1i(uPCount, 0);
      gl.uniform2fv(uPointers, [0, 0]);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      animationFrameRef.current = requestAnimationFrame(loop);
    };
    animationFrameRef.current = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('pointermove', onMove);
      cancelAnimationFrame(animationFrameRef.current);
      gl.deleteProgram(program);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      gl.deleteBuffer(buf);
    };
  }, []);

  return canvasRef;
};

// ─── Hero Component ──────────────────────────────────────────────────────────

const Hero = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canvasRef = useShaderBackground();

  const scrollToServices = () => {
    document.getElementById('servicios')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-[#0F1C2E]"
    >
      {/* ── WebGL Canvas ── */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full touch-none"
        style={{ background: '#0F1C2E' }}
      />

      {/* ── Dark overlay for text readability ── */}
      <div className="absolute inset-0 bg-[#0F1C2E]/45 pointer-events-none" />

      {/* ── Content ── */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="max-w-3xl">

          {/* Trust badge */}
          <div
            className="flex items-center gap-3 mb-6"
            style={{ animation: 'mmFadeInDown 0.8s ease-out forwards' }}
          >
            <div className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#4EBFBF]/30 bg-[#4EBFBF]/10 backdrop-blur-sm">
              <div className="w-2 h-2 bg-[#F2785C]" style={{ transform: 'rotate(45deg)' }} />
              <span className="text-sm font-semibold text-[#4EBFBF] tracking-widest uppercase font-poppins">
                Puerto Rico & USA
              </span>
            </div>
          </div>

          {/* Headline */}
          <div
            className="mb-6"
            style={{ animation: 'mmFadeInUp 0.8s 0.2s ease-out both' }}
          >
            <h1
              data-testid="hero-headline"
              className="font-poppins font-black text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight"
            >
              <span
                className="block"
                style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #7D468C 50%, #4EBFBF 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {/* Split headline into two gradient lines */}
                {t('hero.headline').split(' ').slice(0, Math.ceil(t('hero.headline').split(' ').length / 2)).join(' ')}
              </span>
              <span
                className="block"
                style={{
                  background: 'linear-gradient(135deg, #4EBFBF 0%, #F2785C 60%, #7D468C 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {t('hero.headline').split(' ').slice(Math.ceil(t('hero.headline').split(' ').length / 2)).join(' ')}
              </span>
            </h1>
          </div>

          {/* Subtitle */}
          <div style={{ animation: 'mmFadeInUp 0.8s 0.4s ease-out both' }}>
            <p className="text-lg sm:text-xl text-white/75 mb-10 max-w-2xl leading-relaxed font-source-sans">
              {t('hero.subheadline')}
            </p>
          </div>

          {/* CTA Buttons */}
          <div
            className="flex flex-wrap gap-4"
            style={{ animation: 'mmFadeInUp 0.8s 0.6s ease-out both' }}
          >
            <button
              data-testid="hero-cta-work"
              onClick={() => navigate('/portafolio')}
              className="group flex items-center gap-2 px-8 py-4 rounded-full font-poppins font-semibold text-sm tracking-wide uppercase text-white transition-[transform,box-shadow,background-color,border-color,color,opacity,gap,backdrop-filter] duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#7D468C]/30"
              style={{
                background: 'linear-gradient(135deg, #7D468C, #4EBFBF)',
              }}
            >
              {t('hero.cta_work')}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              data-testid="hero-cta-talk"
              onClick={() => navigate('/contacto')}
              className="px-8 py-4 rounded-full border-2 border-[#4EBFBF]/60 text-[#4EBFBF] font-poppins font-semibold text-sm tracking-wide uppercase backdrop-blur-sm bg-[#4EBFBF]/10 hover:bg-[#4EBFBF]/20 hover:border-[#4EBFBF] transition-[transform,box-shadow,background-color,border-color,color,opacity,gap,backdrop-filter] duration-300 hover:-translate-y-1"
            >
              {t('hero.cta_talk')}
            </button>
          </div>

          {/* Stats row */}
          <div
            className="flex flex-wrap gap-10 mt-16 pt-10 border-t border-white/10"
            style={{ animation: 'mmFadeInUp 0.8s 0.8s ease-out both' }}
          >
            {[
              { num: '150+', label: t('hero.stat_projects') },
              { num: '8+',   label: t('hero.stat_years') },
              { num: '50+',  label: t('hero.stat_clients') },
            ].map((stat, i) => (
              <div key={i}>
                <div className="font-poppins font-black text-3xl text-[#F2785C]">{stat.num}</div>
                <div className="text-sm text-white/50 font-source-sans mt-0.5">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Scroll cue ── */}
      <button
        onClick={scrollToServices}
        data-testid="hero-scroll-cue"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/30 hover:text-[#4EBFBF] transition-colors z-10"
      >
        <div className="w-px h-8 bg-current" />
        <div className="w-2 h-2 bg-current" style={{ transform: 'rotate(45deg)' }} />
      </button>

      {/* ── Keyframes ── */}
      <style>{`
        @keyframes mmFadeInDown {
          from { opacity:0; transform:translateY(-20px); }
          to   { opacity:1; transform:translateY(0); }
        }
        @keyframes mmFadeInUp {
          from { opacity:0; transform:translateY(30px); }
          to   { opacity:1; transform:translateY(0); }
        }
      `}</style>
    </section>
  );
};

export default Hero;
